/*
  Коммуникации — §3.5, §7.8.1.

  Экран отвечает оператору на один вопрос: чего не хватает, чтобы связаться.
  «Канал не настроен» ответом не считается — заказчику из сегмента с низкой
  технической зрелостью непонятно, что делать, и он позвонит нам вместо того,
  чтобы заполнить поле. Поэтому показываются все незаполненные поля сразу и
  инструкция, пришедшая с сервера.

  Обзвон и рассылка - вариант Б «Таблица», решение владельца 06.10.2026:
  таблица должников с галочками и отметкой «можно звонить», полоса «Выбрано N:
  Позвонить / Отправить СМС», окно с разбором и подтверждением и карточка хода
  обзвона сверху. Прежние карточки «Обзвон» (только разбор, без запуска) и
  «Рассылка сообщений» (всей зоне досудебной работы сразу) заменены ею: обе
  отвечали «сколько», но не давали выбрать «кому».

  **Самих текстов здесь нет — решение владельца 08.09.2026** (пункт 5: убрать
  управление голосом и сообщениями из кабинета). Сценарий разговора и тексты
  сообщений уходят наружу от имени заказчика, но подписывает их юрист платформы,
  и читаются они там же, где подписываются, — в панели владельца. Показывать
  неподписанный текст в кабинете значит предлагать его согласовать тому, кто
  этого не решает.
*/

import { useCallback, useEffect, useState } from 'react'

import { api } from '../api/client.js'
import CallWaveCard from '../components/CallWaveCard.jsx'
import CallWaveDialog from '../components/CallWaveDialog.jsx'
import { Loading, Notice, Tag } from '../components/Common.jsx'
import ContactHistory from '../components/ContactHistory.jsx'
import DebtorTable from '../components/DebtorTable.jsx'
import SmsWaveDialog from '../components/SmsWaveDialog.jsx'
import StuckActions from '../components/StuckActions.jsx'
import useCallWave from '../components/useCallWave.js'
import useDebtorList from '../components/useDebtorList.js'

function Channel({ channel, forwardUri }) {
  const title = channel.kind === 'voice' ? 'Голос' : 'Сообщения'
  return (
    <div className="card">
      <div className="row">
        <div className="section__title">{title}</div>
        <Tag kind={channel.is_ready ? 'calm' : 'warn'}>{channel.health_title}</Tag>
        {channel.mode_title ? <span className="muted">{channel.mode_title}</span> : null}
      </div>

      {channel.is_ready ? (
        <div className="muted">Канал готов: можно ставить обращения в очередь.</div>
      ) : (
        <Notice kind="warn">
          {/* Отказ оператора - то же слово, что в настройках линии (аудит 26.10.2026,
              расхождение 9): «Голос готов» рядом с «оператор не видит регистрацию» не бывает. */}
          <div className="notice__what">
            {channel.problem
              ? `Оператор не подтверждает подключение: ${channel.problem}`
              : `Не хватает: ${channel.missing.join(', ')}.`}
          </div>
          <div className="notice__do">{channel.instruction}</div>
        </Notice>
      )}

      {channel.kind === 'voice' && channel.mode === 'forward' ? (
        <div className="log">
          <div className="log__row">
            <span className="log__when">Адрес переадресации</span>
            <span>{forwardUri}</span>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default function CommunicationsPage() {
  const [channels, setChannels] = useState(null)
  const [error, setError] = useState(null)
  // Растёт после запуска обзвона или рассылки: «Последние обращения» перечитываются.
  const [historyVersion, setHistoryVersion] = useState(0)
  // Какое окно открыто ('call' | 'sms') и чем кончилась последняя рассылка:
  // успешная отправка закрывает окно, и итог показывается над таблицей.
  const [dialog, setDialog] = useState(null)
  const [sent, setSent] = useState(null)
  const callWave = useCallWave()
  const debtors = useDebtorList()

  const load = useCallback(() => {
    setError(null)
    api.channels().then(setChannels).catch(setError)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Успех закрывает окно и сразу обновляет всё, что от него зависит: карточку
  // хода, отметки в таблице (предел частоты израсходован) и последние обращения.
  const { refresh: refreshWave } = callWave
  const { clear, reload } = debtors
  const done = useCallback(
    (result) => {
      setDialog(null)
      setSent(result ?? null)
      clear()
      reload()
      refreshWave()
      load()
      setHistoryVersion((value) => value + 1)
    },
    [clear, reload, refreshWave, load],
  )

  if (error && !channels) return <Notice error={error} />
  if (!channels) return <Loading what="Проверяем каналы" />

  return (
    <>
      <div className="head">
        <div className="head__title">Коммуникации</div>
        <div className="head__note">
          Номера и учётные записи оформляете вы: своих номеров Арбитр ЖКХ не выдаёт.
        </div>
      </div>

      <Notice error={error} />

      {/* Вариант Б макетов, выбор владельца 05.10.2026: плашка под шапкой,
          только когда есть что выяснять у поставщика связи. */}
      <StuckActions />

      <div className="grid">
        <Channel channel={channels.voice} forwardUri={channels.forward_uri} />
        <Channel channel={channels.sms} forwardUri={channels.forward_uri} />
      </div>

      {callWave.wave ? (
        <CallWaveCard
          wave={callWave.wave}
          onStop={callWave.stop}
          stopping={callWave.stopping}
          error={callWave.error}
          stopError={callWave.stopError}
        />
      ) : (
        <Notice error={callWave.error} />
      )}

      {sent ? (
        <Notice kind={sent.accepted ? 'ok' : 'warn'}>
          Отправлено сообщений: {sent.accepted}. Не принято оператором связи: {sent.failed}.
          Подробности - в «Последних обращениях».
        </Notice>
      ) : null}

      <DebtorTable
        debtors={debtors}
        onCall={() => setDialog('call')}
        onSms={() => setDialog('sms')}
      />

      {dialog === 'call' ? (
        <CallWaveDialog
          chosen={debtors.chosen}
          list={debtors.list}
          onClose={() => setDialog(null)}
          onStarted={() => done(null)}
        />
      ) : null}
      {dialog === 'sms' ? (
        <SmsWaveDialog
          chosen={debtors.chosen}
          onClose={() => setDialog(null)}
          onSent={done}
        />
      ) : null}

      <ContactHistory version={historyVersion} />
    </>
  )
}
